import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  CalendarDaysIcon,
  ClockIcon,
  UserIcon,
  VideoCameraIcon,
  ArrowTopRightOnSquareIcon,
  PlusIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  LinkIcon
} from '@heroicons/react/24/outline';
import {
  getIntegrationsStatus,
  getGoogleCalendarEvents,
  getCalendlyEvents,
  deleteGoogleCalendarEvent,
  getTalentPool
} from '../services/api';

function Calendar() {
  const [integrations, setIntegrations] = useState({
    calendly: { connected: false },
    googleCalendar: { connected: false }
  });
  const [googleEvents, setGoogleEvents] = useState([]);
  const [calendlyEvents, setCalendlyEvents] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('upcoming');
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load integration status
      const statusResponse = await getIntegrationsStatus();
      if (statusResponse.data.success) {
        setIntegrations(statusResponse.data.integrations);

        // Load events based on connected integrations
        const promises = [];

        if (statusResponse.data.integrations.googleCalendar?.connected) {
          promises.push(loadGoogleEvents());
        }

        if (statusResponse.data.integrations.calendly?.connected) {
          promises.push(loadCalendlyEvents());
        }

        await Promise.all(promises);
      }

      // Load candidates for scheduling
      try {
        const candidatesResponse = await getTalentPool();
        const candidateData = candidatesResponse.data.candidates || candidatesResponse.data || [];
        setCandidates(Array.isArray(candidateData) ? candidateData : []);
      } catch (error) {
        console.error('Failed to load candidates:', error);
      }
    } catch (error) {
      console.error('Failed to load calendar data:', error);
      toast.error('Failed to load calendar data');
    } finally {
      setLoading(false);
    }
  };

  const loadGoogleEvents = async () => {
    try {
      const response = await getGoogleCalendarEvents(20, 'interview');
      if (response.data.success) {
        setGoogleEvents(response.data.events || []);
      }
    } catch (error) {
      console.error('Failed to load Google events:', error);
    }
  };

  const loadCalendlyEvents = async () => {
    try {
      const response = await getCalendlyEvents('active', 20);
      if (response.data.success) {
        setCalendlyEvents(response.data.events || []);
      }
    } catch (error) {
      console.error('Failed to load Calendly events:', error);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to cancel this interview?')) return;

    try {
      const response = await deleteGoogleCalendarEvent(eventId);
      if (response.data.success) {
        toast.success('Interview cancelled');
        setGoogleEvents(prev => prev.filter(e => e.id !== eventId));
      }
    } catch (error) {
      console.error('Failed to delete event:', error);
      toast.error('Failed to cancel interview');
    }
  };

  const formatEventTime = (event) => {
    const start = new Date(event.start?.dateTime || event.start?.date);
    const end = new Date(event.end?.dateTime || event.end?.date);

    const dateOptions = { weekday: 'short', month: 'short', day: 'numeric' };
    const timeOptions = { hour: 'numeric', minute: '2-digit' };

    const dateStr = start.toLocaleDateString('en-US', dateOptions);
    const startTime = start.toLocaleTimeString('en-US', timeOptions);
    const endTime = end.toLocaleTimeString('en-US', timeOptions);

    return { dateStr, timeStr: `${startTime} - ${endTime}` };
  };

  const isEventPast = (event) => {
    const end = new Date(event.end?.dateTime || event.end?.date);
    return end < new Date();
  };

  const allEvents = [
    ...googleEvents.map(e => ({ ...e, source: 'google' })),
    ...calendlyEvents.map(e => ({ ...e, source: 'calendly' }))
  ].sort((a, b) => {
    const dateA = new Date(a.start?.dateTime || a.start?.date || a.start_time);
    const dateB = new Date(b.start?.dateTime || b.start?.date || b.start_time);
    return dateA - dateB;
  });

  const upcomingEvents = allEvents.filter(e => !isEventPast(e));
  const pastEvents = allEvents.filter(e => isEventPast(e));

  const displayEvents = activeTab === 'upcoming' ? upcomingEvents : pastEvents;

  const noIntegrations = !integrations.calendly?.connected && !integrations.googleCalendar?.connected;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto py-8 px-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Interview Calendar</h1>
          <p className="mt-1 text-gray-600">
            View and manage your scheduled interviews.
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <Link
            to="/integrations"
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <LinkIcon className="h-4 w-4 mr-2" />
            Manage Integrations
          </Link>
          {integrations.googleCalendar?.connected && (
            <button
              onClick={() => setShowScheduleModal(true)}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Schedule Interview
            </button>
          )}
        </div>
      </div>

      {/* No Integrations Warning */}
      {noIntegrations && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-8">
          <div className="flex items-start">
            <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600 mr-3 flex-shrink-0" />
            <div>
              <h3 className="text-lg font-medium text-yellow-800">No Calendar Connected</h3>
              <p className="mt-1 text-sm text-yellow-700">
                Connect Calendly or Google Calendar to start scheduling interviews.
              </p>
              <Link
                to="/integrations"
                className="mt-3 inline-flex items-center text-sm font-medium text-yellow-800 hover:text-yellow-900"
              >
                Connect your calendar
                <ArrowTopRightOnSquareIcon className="h-4 w-4 ml-1" />
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Integration Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <div className={`p-4 rounded-lg border ${integrations.calendly?.connected ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <CalendarDaysIcon className={`h-5 w-5 ${integrations.calendly?.connected ? 'text-blue-600' : 'text-gray-400'}`} />
              <span className="font-medium text-gray-900">Calendly</span>
            </div>
            <span className={`text-sm ${integrations.calendly?.connected ? 'text-blue-600' : 'text-gray-500'}`}>
              {integrations.calendly?.connected ? 'Connected' : 'Not connected'}
            </span>
          </div>
          {integrations.calendly?.schedulingUrl && (
            <a
              href={integrations.calendly.schedulingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 text-xs text-blue-600 hover:text-blue-800 flex items-center"
            >
              Open scheduling page
              <ArrowTopRightOnSquareIcon className="h-3 w-3 ml-1" />
            </a>
          )}
        </div>

        <div className={`p-4 rounded-lg border ${integrations.googleCalendar?.connected ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <CalendarDaysIcon className={`h-5 w-5 ${integrations.googleCalendar?.connected ? 'text-red-600' : 'text-gray-400'}`} />
              <span className="font-medium text-gray-900">Google Calendar</span>
            </div>
            <span className={`text-sm ${integrations.googleCalendar?.connected ? 'text-red-600' : 'text-gray-500'}`}>
              {integrations.googleCalendar?.connected ? 'Connected' : 'Not connected'}
            </span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('upcoming')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'upcoming'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Upcoming ({upcomingEvents.length})
          </button>
          <button
            onClick={() => setActiveTab('past')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'past'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Past ({pastEvents.length})
          </button>
        </nav>
      </div>

      {/* Events List */}
      {displayEvents.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <CalendarDaysIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">
            {activeTab === 'upcoming' ? 'No upcoming interviews' : 'No past interviews'}
          </h3>
          <p className="mt-2 text-sm text-gray-500">
            {activeTab === 'upcoming'
              ? 'Schedule an interview to get started.'
              : 'Past interviews will appear here.'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {displayEvents.map((event, index) => {
            const { dateStr, timeStr } = event.source === 'calendly'
              ? {
                  dateStr: new Date(event.start_time).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
                  timeStr: `${new Date(event.start_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })} - ${new Date(event.end_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`
                }
              : formatEventTime(event);

            const meetLink = event.hangoutLink || event.location?.join_url;
            const attendees = event.attendees || event.invitees_counter?.active || 0;

            return (
              <div
                key={event.id || event.uri || index}
                className={`bg-white rounded-lg border shadow-sm p-5 ${
                  activeTab === 'past' ? 'opacity-75' : ''
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <h3 className="text-lg font-medium text-gray-900">
                        {event.summary || event.name || 'Interview'}
                      </h3>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        event.source === 'google' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'
                      }`}>
                        {event.source === 'google' ? 'Google' : 'Calendly'}
                      </span>
                    </div>

                    <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-gray-500">
                      <div className="flex items-center">
                        <CalendarDaysIcon className="h-4 w-4 mr-1.5" />
                        {dateStr}
                      </div>
                      <div className="flex items-center">
                        <ClockIcon className="h-4 w-4 mr-1.5" />
                        {timeStr}
                      </div>
                      {attendees > 0 && (
                        <div className="flex items-center">
                          <UserIcon className="h-4 w-4 mr-1.5" />
                          {typeof attendees === 'number' ? `${attendees} attendee${attendees !== 1 ? 's' : ''}` : `${attendees.length} attendees`}
                        </div>
                      )}
                    </div>

                    {event.description && (
                      <p className="mt-2 text-sm text-gray-600 line-clamp-2">
                        {event.description}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center space-x-2 ml-4">
                    {meetLink && (
                      <a
                        href={meetLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700"
                      >
                        <VideoCameraIcon className="h-4 w-4 mr-1.5" />
                        Join
                      </a>
                    )}
                    {event.htmlLink && (
                      <a
                        href={event.htmlLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                      >
                        <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                      </a>
                    )}
                    {event.source === 'google' && activeTab === 'upcoming' && (
                      <button
                        onClick={() => handleDeleteEvent(event.id)}
                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Schedule Modal */}
      {showScheduleModal && (
        <ScheduleInterviewModal
          candidates={candidates}
          onClose={() => {
            setShowScheduleModal(false);
            setSelectedCandidate(null);
          }}
          onSuccess={() => {
            setShowScheduleModal(false);
            setSelectedCandidate(null);
            loadGoogleEvents();
          }}
          selectedCandidate={selectedCandidate}
        />
      )}
    </div>
  );
}

// Schedule Interview Modal Component
function ScheduleInterviewModal({ candidates, onClose, onSuccess, selectedCandidate }) {
  const [form, setForm] = useState({
    candidateId: selectedCandidate?._id || '',
    title: '',
    date: '',
    startTime: '',
    endTime: '',
    description: '',
    addMeet: true,
    sendInvites: true
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.candidateId || !form.date || !form.startTime || !form.endTime) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSubmitting(true);

      const startDateTime = new Date(`${form.date}T${form.startTime}`).toISOString();
      const endDateTime = new Date(`${form.date}T${form.endTime}`).toISOString();

      const { createGoogleCalendarEvent } = await import('../services/api');
      const response = await createGoogleCalendarEvent({
        candidateId: form.candidateId,
        title: form.title || undefined,
        description: form.description || undefined,
        startTime: startDateTime,
        endTime: endDateTime,
        addMeet: form.addMeet,
        sendInvites: form.sendInvites
      });

      if (response.data.success) {
        toast.success('Interview scheduled successfully!');
        onSuccess();
      } else {
        toast.error(response.data.error || 'Failed to schedule interview');
      }
    } catch (error) {
      console.error('Schedule error:', error);
      toast.error('Failed to schedule interview');
    } finally {
      setSubmitting(false);
    }
  };

  const selectedCandidateData = candidates.find(c => c._id === form.candidateId);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose} />

        <div className="relative inline-block w-full max-w-lg p-6 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Schedule Interview</h3>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Candidate Select */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Candidate <span className="text-red-500">*</span>
              </label>
              <select
                value={form.candidateId}
                onChange={(e) => setForm({ ...form, candidateId: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                required
              >
                <option value="">Select a candidate</option>
                {candidates.map((candidate) => (
                  <option key={candidate._id} value={candidate._id}>
                    {candidate.fullName || candidate.name} - {candidate.email}
                  </option>
                ))}
              </select>
            </div>

            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title (optional)
              </label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder={selectedCandidateData ? `Interview with ${selectedCandidateData.fullName || selectedCandidateData.name}` : 'Interview'}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            {/* Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                min={new Date().toISOString().split('T')[0]}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>

            {/* Time */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Time <span className="text-red-500">*</span>
                </label>
                <input
                  type="time"
                  value={form.startTime}
                  onChange={(e) => setForm({ ...form, startTime: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Time <span className="text-red-500">*</span>
                </label>
                <input
                  type="time"
                  value={form.endTime}
                  onChange={(e) => setForm({ ...form, endTime: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  required
                />
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description (optional)
              </label>
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={2}
                placeholder="Add any notes for the interview..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            {/* Options */}
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={form.addMeet}
                  onChange={(e) => setForm({ ...form, addMeet: e.target.checked })}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">Add Google Meet link</span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={form.sendInvites}
                  onChange={(e) => setForm({ ...form, sendInvites: e.target.checked })}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">Send email invitations</span>
              </label>
            </div>

            {/* Buttons */}
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                {submitting ? 'Scheduling...' : 'Schedule Interview'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default Calendar;
